import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { toast } from "react-toastify";
import { IArticleRelation, KnowledgeArticleService } from "../../Services/KnowledgeArticleService";
import { IKnowledgeArticleSectionModel } from "../../Services/KnowledgeArticleSectionService";
import {
    isKnowledgeArticleRelationOverview,
    knowledgeArticleRelationDisplayLabelKey,
    knowledgeArticleRelationLabelKey,
    knowledgeArticleRelationPath,
    normalizeKnowledgeArticleRelationSections,
} from "./knowledgeArticleRelationConstants";
import ManageKnowledgeArticleRelationsDialog from "./ManageKnowledgeArticleRelationsDialog";
import "./KnowledgeArticleRelationsCard.scss";

interface KnowledgeArticleRelationsCardProps {
    articleUuid: string;
    /** The article's sections as the page holds them — handed on to the manage dialog. */
    sections: IKnowledgeArticleSectionModel[];
    className?: string;
    titleClassName?: string;
}

/** The article's relations plus their manage dialog. Editor-only: whoever reaches it manages the article —
 *  a reader never sees where an article is linked, and the backend only answers a manager. */
const KnowledgeArticleRelationsCard: React.FC<KnowledgeArticleRelationsCardProps> = ({
    articleUuid,
    sections,
    className,
    titleClassName,
}) => {
    const { t } = useTranslation();
    const knowledgeArticleService = useMemo(() => new KnowledgeArticleService(), []);
    const [relations, setRelations] = useState<IArticleRelation[]>([]);
    const [loading, setLoading] = useState(true);
    const [manageVisible, setManageVisible] = useState(false);

    const loadRelations = useCallback(async () => {
        if (!articleUuid) {
            return;
        }
        setLoading(true);
        try {
            const response = await knowledgeArticleService.fetchRelations(articleUuid);
            setRelations(response.data.data ?? []);
        } catch {
            toast.error(t("knowledgeBase.relations.loadError") as string);
            setRelations([]);
        } finally {
            setLoading(false);
        }
    }, [articleUuid, knowledgeArticleService, t]);

    useEffect(() => {
        void loadRelations();
    }, [loadRelations]);

    return (
        <div className={classNames("knowledge-article-relations-card", className)}>
            <div className="knowledge-article-relations-card__header">
                <span className={titleClassName}>
                    <i className="pi pi-link" />
                    {t("knowledgeBase.relations.title")}
                </span>
                <Button
                    type="button"
                    text
                    size="small"
                    icon="pi pi-cog"
                    label={t("knowledgeBase.relations.manage") as string}
                    disabled={loading}
                    onClick={() => setManageVisible(true)}
                />
            </div>
            {relations.length === 0 ? (
                <p className="knowledge-article-relations-card__empty">{t("knowledgeBase.relations.empty")}</p>
            ) : (
                <ul className="knowledge-article-relations-card__list">
                    {relations.map((relation) => {
                        const path = knowledgeArticleRelationPath(relation.location, relation.locationIdentifier);
                        const typeLabel = t(knowledgeArticleRelationLabelKey(relation.location)) as string;
                        const name = isKnowledgeArticleRelationOverview(relation.locationIdentifier)
                            ? (t("knowledgeBase.relations.overview") as string)
                            : relation.locationName;
                        const selectedSections = normalizeKnowledgeArticleRelationSections(relation.sectionIdentifiers);
                        const sectionsLabel = selectedSections.length === 0
                            ? (t("knowledgeBase.relations.sectionsAll") as string)
                            : `${selectedSections.length} ${t("knowledgeBase.relations.sections")}`;
                        return (
                            <li key={relation.resourceLinkUuid} className="knowledge-article-relations-card__item">
                                <span className="knowledge-article-relations-card__type">
                                    {typeLabel}
                                    {" · "}
                                    {t(knowledgeArticleRelationDisplayLabelKey(relation.displayMode))}
                                    {" · "}
                                    {sectionsLabel}
                                </span>
                                {path ? (
                                    <Link className="knowledge-article-relations-card__link" to={path}>
                                        {name}
                                    </Link>
                                ) : (
                                    <span className="knowledge-article-relations-card__name">{name}</span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            <ManageKnowledgeArticleRelationsDialog
                visible={manageVisible}
                articleUuid={articleUuid}
                sections={sections}
                relations={relations}
                onHide={() => setManageVisible(false)}
                onChanged={loadRelations}
            />
        </div>
    );
};

export default KnowledgeArticleRelationsCard;
